import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText, Download, Eye, Trash2, Upload, Image, File, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const DOCUMENT_TYPE_LABELS = {
  'pan_card': 'PAN Card',
  'aadhar_card': 'Aadhar Card',
  'passport': 'Passport',
  'driving_license': 'Driving License',
  'passport_photo': 'Passport Photo',
  'address_proof': 'Address Proof',
  'education_certificate': 'Education Certificate',
  'degree_certificate': 'Degree Certificate',
  'marksheet': 'Marksheet',
  'experience_letter': 'Experience Letter',
  'relieving_letter': 'Relieving Letter',
  'salary_slip': 'Salary Slip',
  'offer_letter': 'Offer Letter',
  'bank_statement': 'Bank Statement',
  'other': 'Other Document'
};

const getDocumentIcon = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    return <Image className="w-5 h-5 text-blue-500" />;
  }
  if (ext === 'pdf') {
    return <FileText className="w-5 h-5 text-rose-500" />;
  }
  return <File className="w-5 h-5 text-slate-500" />;
};

export default function EmployeeDocumentsTab({ employeeId, caseId }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (employeeId) {
      loadDocuments();
    }
  }, [employeeId]);

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`/documents?employee_id=${employeeId}`);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFullUrl = (fileUrl) => {
    if (!fileUrl) return null;
    // If already a full URL, return as is
    if (fileUrl.startsWith('http')) return fileUrl;
    // Prepend the API base URL
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}${fileUrl}`;
  };

  const handleView = (doc) => {
    const url = getFullUrl(doc.file_url);
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.error('Document URL not available');
    }
  };

  const handleDownload = (doc) => {
    const url = getFullUrl(doc.file_url);
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name || 'document';
      link.click();
    } else {
      toast.error('Document URL not available');
    }
  };

  const groupedDocuments = documents.reduce((acc, doc) => {
    const category = doc.check_type || doc.document_type || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push(doc);
    return acc;
  }, {});

  if (loading) {
    return <div className="p-8 text-center">Loading documents...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">My Documents</h2>
        <p className="text-slate-600">View all documents uploaded for your verification</p>
      </div>

      {/* Summary Card */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">{documents.length}</div>
            <div className="text-sm text-slate-500">Total Documents</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-emerald-600">
              {documents.filter(d => d.verified).length}
            </div>
            <div className="text-sm text-slate-500">Verified</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-amber-600">
              {documents.filter(d => !d.verified).length}
            </div>
            <div className="text-sm text-slate-500">Pending Review</div>
          </CardContent>
        </Card>
      </div>

      {documents.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <FolderOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-600">No Documents Uploaded</h3>
            <p className="text-slate-500 mt-2">Documents you upload during verification will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Uploaded Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Uploaded On</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {getDocumentIcon(doc.file_name)}
                        <div>
                          <div className="font-medium">{doc.file_name || 'Document'}</div>
                          {doc.check_type && (
                            <div className="text-xs text-slate-500">
                              For: {doc.check_type.replace(/_/g, ' ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type?.replace(/_/g, ' ') || 'General'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      {doc.verified ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Verified</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700">Pending Review</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleView(doc)} title="View">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} title="Download">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Document Categories */}
      {Object.keys(groupedDocuments).length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Documents by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(groupedDocuments).map(([category, docs]) => (
                <div key={category} className="p-4 border rounded-lg hover:bg-slate-50">
                  <div className="font-medium text-slate-700 capitalize">
                    {category.replace(/_/g, ' ')}
                  </div>
                  <div className="text-2xl font-bold text-blue-600">{docs.length}</div>
                  <div className="text-xs text-slate-500">documents</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
